<template>
  <section class="s7-hero">
    <!-- Top Header: Title and Trust Checkmarks -->
    <div class="s7-top">
      <h1 class="s7-h">
        Find the right copier <span>for your office</span>
      </h1>
      <ul class="s7-trust">
        <li>
          <svg
            class="s7-tick"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="m5 12.5 4.2 4.2L19 7"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
          Free quote, no obligation
        </li>
        <li>
          <svg
            class="s7-tick"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="m5 12.5 4.2 4.2L19 7"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
          Every major brand
        </li>
        <li>
          <svg
            class="s7-tick"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="m5 12.5 4.2 4.2L19 7"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
          Local technicians
        </li>
      </ul>
    </div>

    <!-- Hero Cards Grid (C8: Help Me Choose + 3 Navy Photo Tiles) -->
    <div class="s7-tiles">
      <!-- 1. Help Me Choose Banner -->
      <div
        class="s7-hb"
        @click="HandleHelpClick">
        <div class="s7-pad">
          <img
            alt="Office copier"
            :src="C8AssetImage('hero_copier_pad.png')">
        </div>
        <div class="s7-txt">
          <span class="s7-t">Help me choose</span>
          <div
            class="s7-ai"
            @click.stop="HandleAiClick">
            <svg
              class="s7-spark"
              aria-hidden="true"
              viewBox="0 0 24 24">
              <path
                d="M12 0C12 6.627 6.627 12 0 12C6.627 12 12 17.373 12 24C12 17.373 17.373 12 24 12C17.373 12 12 6.627 12 0Z"
                fill="#ffffff"
                stroke="#000000"
                stroke-width="0.8">
              </path>
            </svg>
            <span class="s7-ai-desktop">or ask our AI</span>
            <span class="s7-ai-mobile">Ask our AI</span>
          </div>
        </div>
        <button
          type="button"
          class="s7-go"
          aria-label="Help me choose">
          <svg
            class="s7-arrow"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
        </button>
      </div>

      <!-- 2. Lease Deals Photo Tile -->
      <div
        class="s7-q s7-lease"
        @click="HandleLeaseDeals">
        <img
          alt="Copier leasing"
          :src="C8AssetImage('tile_lease.jpg')">
        <div class="s7-q-label">
          <span class="s7-lbl-desktop">Overpaying on your lease?</span>
          <span class="s7-lbl-mobile">Overpaying on your lease?</span>
          <svg
            class="s7-arrow-sm"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
        </div>
      </div>

      <!-- 3. Service & Repair Photo Tile -->
      <div
        class="s7-q s7-svc"
        @click="HandleServiceContract">
        <img
          alt="Copier service"
          :src="C8AssetImage('tile_service.jpg')">
        <div class="s7-q-label">
          <span class="s7-lbl-desktop">Service &amp; repair</span>
          <span class="s7-lbl-mobile">Service</span>
          <svg
            class="s7-arrow-sm"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
        </div>
      </div>

      <!-- 4. Supplies & Toner Photo Tile -->
      <div
        class="s7-q s7-sup"
        @click="HandleSupplies">
        <img
          alt="Copier toner and supplies"
          :src="C8AssetImage('tile_supplies.jpg')">
        <div class="s7-q-label">
          <span class="s7-lbl-desktop">Supplies &amp; toner</span>
          <span class="s7-lbl-mobile">Shop supplies</span>
          <svg
            class="s7-arrow-sm"
            aria-hidden="true"
            viewBox="0 0 24 24">
            <path
              d="M5 12h14M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
            </path>
          </svg>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const emit = defineEmits<{
  (e: 'show-deals'): void
  (e: 'open-service-contract'): void
  (e: 'show-supplies'): void
  (e: 'open-assistant'): void
  (e: 'open-quiz'): void
}>()

const rel_path = (window as any).catalog_config?.rel_path || '/wp-content/plugins/1-800-showroom'

function C8AssetImage(filename: string): string {
  return `${rel_path}/assets/img/c8/${filename}`
}

function HandleHelpClick() {
  emit('open-quiz')
}

function HandleAiClick() {
  emit('open-assistant')
}

function HandleLeaseDeals() {
  window.location.href = 'https://1800officesolutions.com/ai-powered-copier-lease-saving-calculator/'
}

function HandleServiceContract() {
  emit('open-service-contract')
}

function HandleSupplies() {
  emit('show-supplies')
}
</script>

<style scoped lang="scss">
.s7-hero {
  margin-bottom: 24px;
}

.s7-top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 18px;
}

.s7-h {
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 38px;
  line-height: 1.06;
  font-weight: 800;
  letter-spacing: -0.035em;
  color: #0b1220;
  margin: 0;

  span {
    background: linear-gradient(90deg, #1b5fd1, #7c3aed);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
}

.s7-trust {
  display: flex;
  align-items: center;
  gap: 22px;
  list-style: none;
  padding: 0 0 4px;
  margin: 0;

  li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13.5px;
    font-weight: 500;
    color: #475569;
    white-space: nowrap;
  }

  .s7-tick {
    width: 15px;
    height: 15px;
    color: #15803d;
    flex-shrink: 0;
  }
}

.s7-tiles {
  display: grid;
  grid-template-columns: minmax(360px, 2fr) repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.s7-hb {
  position: relative;
  display: flex;
  align-items: center;
  gap: 20px;
  height: 116px;
  padding: 0 20px 0 10px;
  border-radius: 18px;
  background: linear-gradient(115deg, #1b5fd1 0%, #164cb4 45%, #0b2a6f 100%);
  background-size: 130% 100%;
  background-position: 0 0;
  color: #ffffff;
  overflow: hidden;
  cursor: pointer;
  transition: background-position 0.4s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 14px rgba(11, 42, 111, 0.12);

  &:hover {
    background-position: 100% 0;
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(11, 42, 111, 0.22);

    .s7-go {
      transform: translateX(4px);
    }
  }
}

.s7-pad {
  flex: none;
  align-self: stretch;
  margin: 10px 0;
  width: 124px;
  border-radius: 12px;
  background: #ffffff;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;

  img {
    display: block;
    height: 82px;
    width: auto;
    max-width: none;
    object-fit: contain;
  }
}

.s7-txt {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.s7-t {
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 19px;
  line-height: 1.25;
  font-weight: 700;
  letter-spacing: -0.012em;
  color: #ffffff;
  white-space: nowrap;
}

.s7-ai {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  line-height: 1.2;
  font-weight: 600;
  color: #93c5fd;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover span {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .s7-spark {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }
}

.s7-go {
  margin-left: auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ffffff;
  color: #164cb4;
  display: grid;
  place-items: center;
  flex: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;

  .s7-arrow {
    width: 18px;
    height: 18px;
  }
}

.s7-q {
  position: relative;
  display: block;
  height: 116px;
  border-radius: 18px;
  overflow: hidden;
  isolation: isolate;
  background: #0b2a6f;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(11, 42, 111, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      to top,
      rgba(11, 42, 111, 1) 0%,
      rgba(11, 42, 111, 0.96) 6%,
      rgba(11, 42, 111, 0.84) 18%,
      rgba(11, 42, 111, 0.64) 31%,
      rgba(11, 42, 111, 0.34) 43%,
      rgba(11, 42, 111, 0.12) 56%,
      rgba(11, 42, 111, 0.06) 70%,
      rgba(11, 42, 111, 0.06) 100%
    );
  }

  .s7-q-label {
    position: absolute;
    left: 16px;
    bottom: 14px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 7px;
    font-family: Inter, system-ui, -apple-system, sans-serif;
    font-size: 15px;
    line-height: 18px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: #ffffff;
    white-space: nowrap;

    .s7-arrow-sm {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(11, 42, 111, 0.2);

    img {
      transform: scale(1.05);
    }

    .s7-q-label .s7-arrow-sm {
      transform: translateX(4px);
    }
  }
}

.s7-lease {
  img {
    object-position: 50% 68%;
    filter: saturate(0.85);
  }

  .s7-q-label {
    right: 12px;
    white-space: normal;
    font-size: 13.5px;
    line-height: 16px;
  }
}

.s7-svc img {
  object-position: 50% 58%;
}

.s7-sup img {
  object-position: 50% 100%;
  filter: saturate(0.85);
}

.s7-lbl-mobile,
.s7-ai-mobile {
  display: none !important;
}
.s7-lbl-desktop,
.s7-ai-desktop {
  display: inline !important;
}

@media (max-width: 1024px) {
  .s7-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .s7-h {
    font-size: 30px;
  }

  .s7-tiles {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .s7-tiles {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .s7-hb {
    grid-column: 1 / -1;
    height: 100px;
    padding: 0 16px 0 8px;
    border-radius: 16px;
    gap: 12px;
  }

  .s7-q {
    display: block !important;
    height: 94px;
    border-radius: 14px;

    .s7-q-label {
      left: 4px;
      right: 4px;
      bottom: 10px;
      justify-content: center;
      text-align: center;
      font-size: 13px;
      line-height: 15px;
      font-weight: 700;
      white-space: normal;
      padding: 0;

      .s7-arrow-sm {
        display: none !important;
      }
    }
  }

  .s7-lbl-desktop,
  .s7-ai-desktop {
    display: none !important;
  }

  .s7-lbl-mobile,
  .s7-ai-mobile {
    display: inline !important;
  }
}

@media (max-width: 640px) {
  .s7-hero {
    margin-bottom: 16px;
  }

  .s7-top {
    margin-bottom: 12px;
  }

  .s7-h {
    font-size: 22px;
  }

  .s7-trust {
    flex-wrap: wrap;
    gap: 6px 14px;

    li {
      font-size: 12px;
    }
  }

  .s7-pad {
    width: 86px;

    img {
      height: 58px;
    }
  }

  .s7-t {
    font-size: 17px;
  }
}

@media (max-width: 480px) {
  .s7-tiles {
    gap: 6px;
  }

  .s7-hb {
    height: 88px;
    padding: 0 12px 0 6px;
    border-radius: 12px;
    gap: 10px;
  }

  .s7-pad {
    width: 74px;
    margin: 6px 0;

    img {
      height: 50px;
    }
  }

  .s7-t {
    font-size: 15px;
  }

  .s7-ai {
    font-size: 11.5px;
  }

  .s7-go {
    width: 32px;
    height: 32px;

    .s7-arrow {
      width: 14px;
      height: 14px;
    }
  }

  .s7-q {
    height: 84px;
    border-radius: 12px;

    .s7-q-label {
      font-size: 11px;
      line-height: 13px;
      bottom: 8px;
      left: 2px;
      right: 2px;
    }
  }
}
</style>
